import { chakra } from '@chakra-ui/react';
import { Icon } from '../icons/Icon';
import { focusRing } from '../primitives/_shared';
import { isActivePath, type LinkComponent, type NavModelItem } from './types';

const Bar = chakra('nav', {
  base: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'space-around',
    width: '100%',
    bg: 'surface.raised',
    borderTopWidth: '1px',
    borderColor: 'border.subtle',
    // Respect the iOS home-indicator safe area.
    pb: 'env(safe-area-inset-bottom)',
    boxShadow: 'md',
  },
});

const Tab = chakra('a', {
  base: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1',
    minH: '14', // 56px — full-height tap target (>=44px)
    py: '1.5',
    color: 'text.muted',
    textDecoration: 'none',
    fontSize: '2xs',
    fontWeight: 'medium',
    lineHeight: 'none',
    cursor: 'pointer',
    transitionProperty: 'color',
    transitionDuration: 'fast',
    _hover: { color: 'text.secondary' },
    '&[data-active="true"]': { color: 'action.primary', fontWeight: 'semibold' },
    ...focusRing,
  },
});

export interface BottomNavProps {
  items: NavModelItem[];
  currentPath: string;
  linkComponent?: LinkComponent;
  onNavigate?: () => void;
}

/**
 * Mobile/tablet primary navigation — a genuine bottom tab bar (not a squished rail).
 * Each tab is a full-height (>=44px) link with the same accessible name as its rail
 * counterpart; the active tab is derived from `currentPath`.
 */
export function BottomNav({ items, currentPath, linkComponent, onNavigate }: BottomNavProps) {
  return (
    <Bar aria-label="Primary">
      {items.map((item) => {
        const active = isActivePath(currentPath, item.to);
        const linkProps = linkComponent ? { to: item.to } : { href: item.to };
        return (
          <Tab
            key={item.key}
            as={linkComponent}
            {...linkProps}
            onClick={onNavigate}
            aria-label={item.label}
            aria-current={active ? 'page' : undefined}
            data-active={active ? 'true' : undefined}
            data-testid={item['data-testid']}
          >
            <Icon as={item.icon} size="md" tone="current" />
            <chakra.span>{item.label}</chakra.span>
          </Tab>
        );
      })}
    </Bar>
  );
}
